// Adding up the activity durations.
//
// The duration cells are free text and always have been — instructors type
// "15", but also "15 min", "10-15", "~20" and "TBD". Parsing has to be
// forgiving about all of that rather than demanding a number, so the total is
// built from the first number in each cell and cells with no number in them
// simply do not contribute.

/**
 * Minutes in one duration cell, or 0 when there is no number in it.
 *
 * A range ("10-15") counts as its lower bound: that is the number the
 * instructor is committing to, and quietly counting the upper bound would
 * inflate the total of a plan built entirely out of ranges.
 */
export const parseDurationMinutes = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : 0;
  if (typeof value !== 'string') return 0;
  const match = /\d+(?:\.\d+)?/.exec(value);
  if (!match) return 0;
  const minutes = Number.parseFloat(match[0]);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
};

/** Total minutes across every activity in a plan. */
export const totalActivityMinutes = (activities) => {
  if (!Array.isArray(activities)) return 0;
  return activities.reduce((total, activity) => total + parseDurationMinutes(activity && activity.duration), 0);
};

/**
 * Minutes as hours and minutes: "45 min", "1 hr", "1 hr 30 min", "2 hr 5 min".
 *
 * Rounded to the whole minute — someone typing "12.5" wants half a minute
 * counted in the total, not shown back to them as "37.5 min" in a summary row.
 */
export const formatDuration = (minutes) => {
  const total = Math.round(Number(minutes) || 0);
  if (total <= 0) return '0 min';
  const hours = Math.floor(total / 60);
  const remainder = total % 60;
  if (!hours) return `${remainder} min`;
  if (!remainder) return `${hours} hr`;
  return `${hours} hr ${remainder} min`;
};

/** The total for a plan, ready to display. */
export const formatTotalActivityTime = (activities) => formatDuration(totalActivityMinutes(activities));
